import {InvoicePayment} from '@/model/billing/InvoicePayment'
import {InvoicePaymentStatus} from '@/types/enums'
import moment from 'moment'
import {TranslateResult} from 'vue-i18n'
import i18n from '@/plugins/i18n'
import {formatBoarderBankIdentificationCode, formatIban, formatIntPrice} from '@/services/formatter'
import Boarder from '@/model/boarder/Boarder'
import _ from 'lodash'

export class Invoice {
  private constructor(
    readonly id: string,
    readonly customer: string,
    readonly issueDate: Date,
    readonly dueDate: Date,
    readonly iban: string,
    readonly boarderBankIdentificationCode: number,
    readonly specificSymbol: string,
    readonly constantSymbol: string,
    readonly periodKind: string,
    readonly name: string,
    readonly currency: string,
    readonly priceItemSum: number,
    readonly priceItemSumNatural: number,
    readonly amountFromWallet: number,
    readonly amountFromDept: number,
    readonly priceTotal: number,
    readonly invoicePayments: InvoicePayment[],
    readonly periodStart: Date,
    readonly periodEnd: Date,
    readonly paymentStatus: InvoicePaymentStatus,
    readonly amountAlreadyPaid: number,
    readonly amountAlreadyPaidNatural: number,
    readonly outstandingAmountToPay: number,
    readonly outstandingAmountToPayNatural: number,
    readonly boarder: Boarder | null
  ) {
  }

  get iri(): string {
    return `/api/invoice/${this.id}`
  }

  get isLate(): boolean {
    return this.dueDate <= new Date()
  }

  get paymentStatusColor(): string {
    let color = 'orange'

    if (this.paymentStatus === InvoicePaymentStatus.notPaid) {
      color = 'orange'
    } else if (this.paymentStatus === InvoicePaymentStatus.fullyPaid) {
      color = 'green'
    } else if (this.paymentStatus === InvoicePaymentStatus.partiallyPaid) {
      color = 'orange'
    } else if (this.paymentStatus === InvoicePaymentStatus.notPayedAfterExpiration) {
      color = 'red'
    }

    return color
  }

  get paymentStatusIcon(): string {
    let icon = 'far fa-question-circle'

    if (this.paymentStatus === InvoicePaymentStatus.notPaid) {
      icon = 'far fa-exclamation-circle'
    } else if (this.paymentStatus === InvoicePaymentStatus.fullyPaid) {
      icon = 'far fa-check-circle'
    } else if (this.paymentStatus === InvoicePaymentStatus.partiallyPaid) {
      icon = 'far fa-exclamation-triangle'
    } else if (this.paymentStatus === InvoicePaymentStatus.notPayedAfterExpiration) {
      icon = 'far fa-exclamation-triangle'
    }

    return icon
  }

  get paymentStatusLabel(): TranslateResult {
    let label: TranslateResult = i18n.t('billingPage.notPayed');

    if (this.paymentStatus === InvoicePaymentStatus.notPaid) {
      label = this.isLate ? i18n.t('billingPage.notPayedAfterExpiration') : i18n.t('billingPage.notPayed')
    } else if (this.paymentStatus === InvoicePaymentStatus.fullyPaid) {
      label = i18n.t('billingPage.fullyPayed')
    } else if (this.paymentStatus === InvoicePaymentStatus.partiallyPaid) {
      label = i18n.t('billingPage.partiallyPayed')
    }

    return label
  }

  get formattedPeriodRange(): string {
    const startFormat = this.periodStart.getFullYear() !== this.periodEnd.getFullYear() ? 'D.M.YYYY' : 'D.M'
    const start = moment(this.periodStart).format(startFormat)
    const end = moment(this.periodEnd).format('D.M.YYYY')

    return `${start} - ${end}`
  }

  get formattedPriceItemSum(): string {
    return formatIntPrice(this.priceItemSum)
  }

  get formattedPriceTotal(): string {
    return formatIntPrice(this.priceTotal)
  }

  get formattedAmountFromDept(): string {
    return formatIntPrice(this.amountFromDept)
  }

  get formattedAmountFromWallet(): string {
    return formatIntPrice(this.amountFromWallet)
  }

  get formattedAmountAlreadyPaid(): string {
    return formatIntPrice(this.amountAlreadyPaid)
  }

  get formattedOutstandingAmountToPay(): string {
    return formatIntPrice(this.outstandingAmountToPay)
  }

  get formattedIban(): string {
    return formatIban(this.iban)
  }

  get formattedBoarderBankIdentificationCode(): string {
    return formatBoarderBankIdentificationCode(this.boarderBankIdentificationCode)
  }

  static createFromResponse(item: any): Invoice {
    let boarder = item.boarder && !_.isString(item.boarder) ? Boarder.createFromModel(item.boarder) : null

    return new Invoice(
      item.id,
      item.customer,
      new Date(item.issueDate),
      new Date(item.dueDate),
      item.iban,
      item.boarderBankIdentificationCode,
      item.specificSymbol,
      item.constantSymbol,
      item.periodKind,
      item.name,
      item.currency,
      item.priceItemSum,
      item.priceItemSum /100,
      item.amountFromWallet,
      item.amountFromDept,
      item.priceTotal,
      item.invoicePayments && InvoicePayment.createFromResponse(item.invoicePayments) || [],
      new Date(item.periodStart),
      new Date(item.periodEnd),
        (item.paymentStatus === 'not-paid' && new Date(item.dueDate) <= new Date() ) ? InvoicePaymentStatus.notPayedAfterExpiration : Invoice.resolvePaymentStatus(item.paymentStatus),
      item.amountAlreadyPayed,
        item.amountAlreadyPayed / 100,
      item.outstandingAmountToPay,
        item.outstandingAmountToPay / 100,
      boarder
    )
  }

  static createListFromResponse(invoiceList: any[]): Invoice[] {
    return invoiceList.map(invoiceItem => Invoice.createFromResponse(invoiceItem))
  }

  private static resolvePaymentStatus(statusFromResponse: string): InvoicePaymentStatus {
    const statusMap: { [key: string]: InvoicePaymentStatus } = {
      'not-paid': InvoicePaymentStatus.notPaid,
      'fully-paid': InvoicePaymentStatus.fullyPaid,
      'partially-paid': InvoicePaymentStatus.partiallyPaid
    }

    return statusMap[statusFromResponse] || InvoicePaymentStatus.notDetermined
  }
}
